import prettier from "prettier/standalone"
import parserHtml from "prettier/parser-html"
import { useEffect, useState, ReactNode } from "react"
import { render, unmountComponentAtNode } from "react-dom"
import { ThemeProvider } from "@stackup/inputs"
import { theme } from "~/forms"
import { Code } from "./Code"

interface Props {
  children: ReactNode
  title?: string
  copy?: boolean
  headingLevel?: number
}

/**
 * Convert React elements to HTML. This will render the React elements into
 * an invisible DOM node. When the component mounts or updates, the HTML will
 * be emitted.
 *
 * This function returns a callback that can be used to unmount the component.
 */
const toHTML = (node: ReactNode, callback: (html: string) => void) => {
  const root = document.createElement("div")
  const tree = <ThemeProvider value={theme}>{node}</ThemeProvider>
  render(tree, root, () => callback(root.innerHTML))

  return () => {
    unmountComponentAtNode(root)
    root.remove()
  }
}

/**
 * Make the HTML pretty.
 */
const formatHTML = (html: string): string => {
  return prettier.format(html, {
    parser: "html",
    plugins: [parserHtml],
    htmlWhitespaceSensitivity: "ignore",
  })
}

export function HTML({ children, ...props }: Props) {
  const [html, setHTML] = useState("")

  useEffect(
    () => toHTML(children, (html) => setHTML(formatHTML(html))),
    [children]
  )

  return <Code lang="html" content={html} {...props} />
}
